// footer/SkillPanel.tsx
// 技能下拉面板（还原 figma node 2840:14213）。
// 由 SkillButton 点击展开，定位/宽度跟随由 SkillButton 负责，本组件只渲染内容。
// 列表数据：优先用外部 skillListFetch（每次面板打开/mount 调一次刷新），未传则用内置 mock。
// 选中交互：点卡片回调 onSelect(已选则再点取消)，selectedId 命中的卡片高亮。
// 卡片 hover 才显示右上角三操作图标（分享/编辑/删除）
// + 右下角「去使用」按钮；删除点击弹 Popconfirm 确认，确认后调 onAction('delete') 等接口完成再刷新列表。
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { App, Popconfirm, Popover, Spin } from 'antd';
import { usePopupHost } from '../PopupHostContext';
import { SeTooltip } from '../nodes/SeTooltip';
import type { SkillItem, SkillListQuery, SkillListResult, SkillType } from '../types';
import { useTextOverflow } from '../utils/useTextOverflow';

// ── mock 数据 ──────────────────────────────────────────────
type SkillBadge = 'system' | 'mine' | 'subscribe';

type SkillCard = {
  id: string;
  title: string;
  desc: string;
  /** 使用说明（选中技能后输入框 placeholder 展示） */
  usageInstruction?: string;
  tags: string[];
  /** 角标类型，决定右下角标签与热度展示 */
  badge: SkillBadge;
  /** 热度（万人正在使用），mine 类不展示热度而展示「去使用」 */
  heat?: string;
};

/** 选中的技能（对外暴露给 SkillButton / index.tsx，用于按钮高亮 + 与智能体互斥） */
export type SkillSelection = { id: string; title: string; desc?: string; usageInstruction?: string };

/** 技能卡片右上角图标类型：分享 / 编辑 / 删除 */
export type SkillCardActionType = 'share' | 'edit' | 'delete';

/** 面板头部按钮类型：导入技能 / 基于会话创建技能 */
export type SkillHeaderActionType = 'import' | 'create';

type Props = {
  /** 当前选中技能 id（null=未选）→ 命中卡片高亮 */
  selectedId?: string | null;
  /** 点卡片回调；再次点已选卡片传 null（取消选中） */
  onSelect?: (skill: SkillSelection | null) => void;
  /** 单独点击卡片主体回调（不回填、不改选中态，仅把技能信息抛给外部）。
   *  与 onSelect 区分：onSelect 现仅由「去使用」按钮触发（回填+高亮）。 */
  onCardClick?: (skill: SkillSelection) => void;
  /** 卡片右上角图标点击回调，action 标识图标类型（分享/编辑/删除）。返回 Promise 时面板 await 后刷新；
   *  分享可返回分享码字符串（或 Promise<string>）供分享弹层填充 */
  onAction?: (action: SkillCardActionType, skill: SkillSelection) => void | string | Promise<void | string>;
  /** 外部技能列表数据源：mount/切 tab/搜索/删除后调用，按 query(skillType/keyword/分页) 返回列表。未传则用内置 mock */
  skillListFetch?: (query: SkillListQuery) => Promise<SkillItem[] | SkillListResult>;
  /** 头部「导入技能」/「基于会话创建技能」按钮点击回调，action 区分是哪个按钮 */
  onHeaderAction?: (action: SkillHeaderActionType) => void;
  /** 是否显示「基于会话创建技能」按钮（智能体激活时隐藏，仅连续对话显示）。默认显示 */
  showCreateSkill?: boolean;
  /** 「基于当前会话创建技能」按钮禁用状态（AI 生成中不允许创建） */
  createDisabled?: boolean;
  /** 面板最大高度，支持 px 数值或 CSS 高度值（默认 360px）；小屏时会按可用空间继续收缩 */
  panelMaxHeight?: string | number;
};

const TABS = ['全部技能', '常用技能', '我的技能', '订阅技能', '系统技能'] as const;

// tab 下标 → 接口 skillType；「全部技能」(下标 0) 传 'all'
const TAB_SKILL_TYPE: SkillType[] = ['all', 'common', 'mine', 'subscribe', 'system'];

const PAGE_SIZE = 20; // 每页条数（接口建议 10~20）

const BADGE_LABEL: Record<SkillBadge, string> = {
  system: '系统技能',
  mine: '我的技能',
  subscribe: '订阅技能',
};

const MOCK_DESC =
  '基于用户上传的抖音推广数据，智能分析高潜力商品与爆品抖音推广数据，智能分析高潜力商品与爆品商品机会';
const MOCK_TAGS = ['淘宝天猫', '抖音电商', '市场选品', '京东商城', '拼多多', '小红书', '快手电商', '视频号'];

const MOCK_USAGE = '当用户需要尖锐吐槽、阴阳怪气评论、犀利差评、回怼话术或讽刺性改写时使用本技能；用户只需提供对象、场景、原话或想表达的不满，即可触发生成。';

const MOCK_CARDS: SkillCard[] = [
  {
    id: 'c1',
    title: '抖音推广找潜力爆品热让抖音推广找潜力爆品热让抖音推广找潜力爆品热让抖音推广找潜力爆品热让抖音推广找潜力爆品热让抖音推广找潜力爆品热让抖音推广找潜力爆品热让抖音推广找潜力爆品热让抖音推广找潜力爆品热让抖音推广找潜力爆品热让抖音推广找潜力爆品热让抖音推广找潜力爆品热让',
    desc: MOCK_DESC,
    tags: MOCK_TAGS,
    badge: 'system',
    heat: '3.5',
    usageInstruction: MOCK_USAGE
  },
  {
    id: 'c2',
    title: '【商品评价明细导出助手】问候提示生成助手',
    desc: MOCK_DESC,
    tags: MOCK_TAGS,
    badge: 'system',
    heat: '6.6',
    usageInstruction: MOCK_USAGE
  },
  {
    id: 'c3',
    title: '抖音推广找潜力爆品',
    desc: MOCK_DESC,
    tags: MOCK_TAGS,
    badge: 'mine',
    usageInstruction: MOCK_USAGE
  },
  {
    id: 'c4',
    title: '抖音推广找潜力爆品',
    desc: MOCK_DESC,
    tags: MOCK_TAGS,
    badge: 'subscribe',
    heat: '6.6',
    usageInstruction: MOCK_USAGE
  },
  {
    id: 'c4',
    title: '抖音推广找潜力爆品',
    desc: MOCK_DESC,
    tags: MOCK_TAGS,
    badge: 'subscribe',
    heat: '6.6',
    usageInstruction: MOCK_USAGE
  },
  {
    id: 'c4',
    title: '抖音推广找潜力爆品',
    desc: MOCK_DESC,
    tags: MOCK_TAGS,
    badge: 'mine',
    heat: '6.6',
    usageInstruction: MOCK_USAGE
  },
  {
    id: 'c4',
    title: '抖音推广找潜力爆品',
    desc: MOCK_DESC,
    tags: MOCK_TAGS,
    badge: 'mine',
    heat: '6.6',
    usageInstruction: MOCK_USAGE
  },
  {
    id: 'c4',
    title: '抖音推广找潜力爆品',
    desc: MOCK_DESC,
    tags: MOCK_TAGS,
    badge: 'mine',
    heat: '6.6',
    usageInstruction: MOCK_USAGE
  },
];

// 把外部 SkillItem 归一化为内部 SkillCard（补默认值，badge 缺省按 system）
const toCard = (s: SkillItem): SkillCard => ({
  id: s.id,
  title: s.title,
  desc: s.desc ?? '',
  usageInstruction: s.usageInstruction,
  tags: s.tags ?? [],
  badge: s.badge ?? 'system',
  heat: s.heat,
});

// ── 图标 ───────────────────────────────────────────────────
const SearchIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M6.2 1a5.2 5.2 0 0 1 4.06 8.45l2.6 2.6a.6.6 0 0 1-.85.85l-2.6-2.6A5.2 5.2 0 1 1 6.2 1Zm0 1.2a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z"
      fill="#4E5969"
    />
  </svg>
);

// 左侧 tab 图标（16×16，fill 用 currentColor 跟随选中态色变）
const TabAllIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M5.99358 8.85714C6.27231 8.85714 6.53962 8.97003 6.73671 9.17096C6.9338 9.37189 7.04453 9.64441 7.04453 9.92857V12.9286C7.04453 13.2127 6.9338 13.4853 6.73671 13.6862C6.53962 13.8871 6.27231 14 5.99358 14H3.05094L2.94375 13.9946C2.6849 13.9676 2.4451 13.8436 2.27076 13.6467C2.09642 13.4497 1.99994 13.1939 2 12.9286V9.92857C1.99994 9.66329 2.09642 9.40742 2.27076 9.21049C2.4451 9.01355 2.6849 8.88956 2.94375 8.8625L3.05094 8.85714H5.99358ZM11.0087 8.74571C11.0323 8.68643 11.0727 8.63567 11.1247 8.59993C11.1768 8.5642 11.2381 8.54511 11.3008 8.54511C11.3636 8.54511 11.4249 8.5642 11.477 8.59993C11.529 8.63567 11.5694 8.68643 11.593 8.74571L12.1521 10.1675C12.168 10.2079 12.1917 10.2446 12.2219 10.2753C12.252 10.3061 12.288 10.3303 12.3276 10.3464L13.8021 10.9486C13.8585 10.9716 13.9072 11.0109 13.9423 11.0615C13.9773 11.1121 13.9973 11.172 13.9997 11.234C14.0022 11.2959 13.987 11.3572 13.956 11.4106C13.9251 11.4639 13.8796 11.507 13.8252 11.5346L12.3024 12.3104C12.2369 12.3438 12.1849 12.3995 12.1553 12.4679L11.5888 13.7675C11.564 13.8242 11.5236 13.8724 11.4725 13.9062C11.4214 13.94 11.3618 13.958 11.3008 13.958C11.2399 13.958 11.1803 13.94 11.1292 13.9062C11.0781 13.8724 11.0377 13.8242 11.0129 13.7675L10.4454 12.4679C10.4158 12.3995 10.3638 12.3438 10.2982 12.3104L8.77753 11.5357C8.7231 11.5081 8.67767 11.465 8.6467 11.4117C8.61574 11.3583 8.60056 11.297 8.603 11.235C8.60545 11.1731 8.62541 11.1132 8.66047 11.0626C8.69554 11.0119 8.74422 10.9727 8.80065 10.9496L10.2751 10.3475C10.3147 10.3313 10.3507 10.3071 10.3809 10.2764C10.411 10.2456 10.4348 10.209 10.4506 10.1686L11.0097 8.74679L11.0087 8.74571ZM3.05094 12.9286H5.99358V9.92857H3.05094V12.9286ZM5.99358 2C6.57371 2 7.04453 2.48 7.04453 3.07143V6.07143C7.04453 6.35559 6.9338 6.62811 6.73671 6.82904C6.53962 7.02997 6.27231 7.14286 5.99358 7.14286H3.05094L2.94375 7.1375C2.6849 7.11044 2.4451 6.98644 2.27076 6.78951C2.09642 6.59258 1.99994 6.33671 2 6.07143V3.07143C1.99994 2.80615 2.09642 2.55028 2.27076 2.35335C2.4451 2.15641 2.6849 2.03242 2.94375 2.00536L3.05094 2H5.99358ZM12.7196 2C12.9984 2 13.2657 2.11288 13.4628 2.31381C13.6598 2.51475 13.7706 2.78727 13.7706 3.07143V6.07143C13.7706 6.35559 13.6598 6.62811 13.4628 6.82904C13.2657 7.02997 12.9984 7.14286 12.7196 7.14286H9.77698L9.66873 7.1375C9.41016 7.10996 9.17075 6.98579 8.99668 6.78893C8.8226 6.59207 8.72619 6.33647 8.72604 6.07143V3.07143C8.72596 2.80632 8.82228 2.55059 8.99641 2.35368C9.17053 2.15677 9.41007 2.03266 9.66873 2.00536L9.77698 2H12.7196ZM9.77698 6.07143H12.7196V3.07143H9.77698V6.07143ZM3.05094 6.07143H5.99358V3.07143H3.05094V6.07143Z" fill="currentColor" />
  </svg>
);

const TabMineIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12.6358 2.44435C12.8831 2.19815 13.2831 2.199 13.5293 2.44624C13.7755 2.69349 13.7746 3.0935 13.5274 3.33968L3.31858 13.5054C3.07133 13.7516 2.67132 13.7508 2.42513 13.5035C2.17893 13.2563 2.17977 12.8563 2.42701 12.6101L12.6358 2.44435Z" fill="currentColor" />
    <path d="M5.27212 5.97815L4.9117 7.22356C4.85382 7.42362 4.64471 7.53887 4.44466 7.48097C4.38352 7.46327 4.32785 7.43039 4.28284 7.38538C4.23783 7.34037 4.20495 7.2847 4.18726 7.22356L3.82686 5.97814C3.76198 5.75394 3.64142 5.54982 3.47639 5.38478C3.31136 5.21975 3.10723 5.09917 2.88304 5.03429L1.6376 4.67391C1.43755 4.61602 1.3223 4.40692 1.3802 4.20687C1.39789 4.14573 1.43077 4.09006 1.47578 4.04505C1.52079 4.00004 1.57646 3.96716 1.6376 3.94946L2.88303 3.58908C3.10722 3.5242 3.31135 3.40363 3.47638 3.23859C3.64141 3.07356 3.76198 2.86943 3.82686 2.64523L4.18726 1.39981C4.24514 1.19975 4.45425 1.08451 4.65429 1.1424C4.71544 1.1601 4.77111 1.19298 4.81612 1.23799C4.86112 1.283 4.89401 1.33867 4.9117 1.39981L5.27209 2.64523C5.33696 2.86943 5.45753 3.07356 5.62257 3.2386C5.7876 3.40363 5.99173 3.5242 6.21593 3.58908L7.46135 3.94946C7.66141 4.00735 7.77666 4.21646 7.71876 4.4165C7.70107 4.47764 7.66818 4.53331 7.62318 4.57832C7.57817 4.62333 7.5225 4.65621 7.46135 4.67391L6.21593 5.03429C5.99173 5.09917 5.7876 5.21974 5.62257 5.38478C5.45753 5.54981 5.33696 5.75394 5.27209 5.97814L5.27212 5.97815ZM12.4587 10.7095L12.3937 10.934C12.3494 11.0869 12.1896 11.175 12.0368 11.1308C11.99 11.1172 11.9475 11.0921 11.9131 11.0577C11.8787 11.0233 11.8536 10.9808 11.8401 10.934L11.7751 10.7095C11.7102 10.4853 11.5896 10.2811 11.4246 10.1161C11.2596 9.95108 11.0554 9.83051 10.8312 9.76564L10.6067 9.70065C10.4538 9.65642 10.3657 9.49662 10.41 9.34376C10.4235 9.29703 10.4486 9.25448 10.483 9.22009C10.5174 9.18569 10.5599 9.16056 10.6067 9.14704L10.8312 9.08206C11.0554 9.01718 11.2596 8.89661 11.4246 8.73158C11.5896 8.56655 11.7102 8.36242 11.7751 8.13823L11.8401 7.91366C11.8843 7.76078 12.0441 7.67272 12.197 7.71694C12.2437 7.73046 12.2862 7.75559 12.3206 7.78999C12.355 7.82439 12.3801 7.86693 12.3937 7.91366L12.4587 8.13823C12.5235 8.36242 12.6441 8.56655 12.8091 8.73158C12.9742 8.89661 13.1783 9.01718 13.4025 9.08206L13.6271 9.14704C13.7799 9.19129 13.868 9.35108 13.8238 9.50394C13.8102 9.55067 13.7851 9.59321 13.7507 9.62761C13.7163 9.662 13.6738 9.68713 13.6271 9.70065L13.4025 9.76564C13.1783 9.83051 12.9742 9.95108 12.8091 10.1161C12.6441 10.2811 12.5235 10.4853 12.4587 10.7095ZM8.73493 13.6947L8.66994 13.9193C8.6257 14.0722 8.46591 14.1602 8.31305 14.116C8.26632 14.1025 8.22378 14.0774 8.18938 14.043C8.15498 14.0086 8.12986 13.966 8.11634 13.9193L8.05135 13.6947C7.98647 13.4705 7.8659 13.2664 7.70087 13.1014C7.53584 12.9363 7.33171 12.8158 7.10752 12.7509L6.88295 12.6859C6.73007 12.6417 6.64201 12.4819 6.68624 12.329C6.69976 12.2823 6.72489 12.2397 6.75928 12.2054C6.79368 12.171 6.83622 12.1458 6.88295 12.1323L7.10752 12.0673C7.33171 12.0024 7.53584 11.8819 7.70087 11.7168C7.8659 11.5518 7.98647 11.3477 8.05135 11.1235L8.11634 10.8989C8.16058 10.746 8.32037 10.658 8.47323 10.7022C8.51996 10.7157 8.5625 10.7409 8.5969 10.7753C8.6313 10.8097 8.65642 10.8522 8.66994 10.8989L8.73493 11.1235C8.7998 11.3477 8.92037 11.5518 9.08541 11.7168C9.25044 11.8819 9.45456 12.0024 9.67876 12.0673L9.90333 12.1323C10.0562 12.1766 10.1443 12.3363 10.1 12.4892C10.0865 12.5359 10.0614 12.5785 10.027 12.6129C9.9926 12.6473 9.95006 12.6724 9.90333 12.6859L9.67876 12.7509C9.45456 12.8158 9.25044 12.9363 9.08541 13.1014C8.92037 13.2664 8.7998 13.4705 8.73493 13.6947Z" fill="currentColor" />
  </svg>
);

const TabCommonIcon: React.FC = () => (
  <i className="iconfont icon-changyongjineng" aria-hidden="true" />
);

const TabSubscribeIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12.2 1.69995H3.8C3.2201 1.69995 2.75 2.17467 2.75 2.76025V13.3632C2.75 13.3754 2.75021 13.3875 2.75061 13.3996C2.76856 13.9361 3.21367 14.2996 3.68722 14.2996C3.86807 14.2996 4.05311 14.2465 4.22003 14.1298L7.39764 11.9084C7.5726 11.7858 7.78102 11.72 7.99466 11.7198C8.2083 11.7197 8.4168 11.7853 8.59191 11.9077L11.7803 14.1308C11.9471 14.2471 12.1318 14.3 12.3124 14.3C12.7861 14.3 13.2314 13.9362 13.2494 13.3996C13.2498 13.3875 13.25 13.3754 13.25 13.3632V2.76025C13.25 2.17467 12.7799 1.69995 12.2 1.69995ZM12.2 13.1434L9.19248 11.0464C8.83927 10.8001 8.42531 10.6699 7.99537 10.6699C7.56449 10.6698 7.14976 10.8005 6.79602 11.0478L3.8 13.1423V2.76025C3.8 2.75802 3.8 2.75322 3.80289 2.74995H12.1971C12.2 2.75322 12.2 2.75801 12.2 2.76025V13.1434Z" fill="currentColor" />
    <path d="M10.4117 6.27223H8.52499V4.36187C8.52499 4.07192 8.28994 3.83687 7.99999 3.83687C7.71004 3.83687 7.47499 4.07192 7.47499 4.36187V6.27223H5.58828C5.29833 6.27223 5.06328 6.50728 5.06328 6.79723V6.80751C5.06328 7.09746 5.29833 7.33251 5.58828 7.33251H7.47499V9.24287C7.47499 9.53282 7.71004 9.76787 7.99999 9.76787C8.28994 9.76787 8.52499 9.53282 8.52499 9.24287V7.33251H10.4117C10.7017 7.33251 10.9367 7.09746 10.9367 6.80751V6.79723C10.9367 6.50728 10.7017 6.27223 10.4117 6.27223Z" fill="currentColor" />
  </svg>
);

const TabSystemIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M11.8231 2.3562H4.19204C3.60373 2.36812 3.04399 2.61212 2.63483 3.03501C2.22567 3.4579 2.00026 4.02539 2.00775 4.61377V9.14983C2.00026 9.73821 2.22567 10.3057 2.63483 10.7286C3.04399 11.1515 3.60373 11.3955 4.19204 11.4074H11.8231C12.4114 11.3955 12.9711 11.1515 13.3803 10.7286C13.7895 10.3057 14.0149 9.73821 14.0074 9.14983V4.61377C14.0149 4.02539 13.7895 3.4579 13.3803 3.03501C12.9711 2.61212 12.4114 2.36812 11.8231 2.3562ZM12.9152 9.12192C12.9176 9.26764 12.8911 9.41238 12.8375 9.54789C12.7839 9.6834 12.704 9.80701 12.6026 9.91167C12.5012 10.0163 12.3802 10.1 12.2464 10.1579C12.1127 10.2157 11.9688 10.2467 11.8231 10.249H4.19204C3.89804 10.2434 3.61817 10.1218 3.41354 9.91067C3.20891 9.6995 3.09616 9.41594 3.09989 9.12192V4.61377C3.09759 4.46822 3.12403 4.32364 3.17771 4.18833C3.23139 4.05303 3.31126 3.92964 3.41272 3.82526C3.51418 3.72088 3.63525 3.63756 3.76898 3.58006C3.90272 3.52257 4.04648 3.49204 4.19204 3.49022H11.8231C12.1165 3.49572 12.3958 3.61686 12.6004 3.82727C12.8049 4.03768 12.9181 4.32034 12.9152 4.61377V9.12192Z" fill="currentColor" />
    <path d="M11.0091 13.6431H5.01099C4.862 13.6431 4.71911 13.5839 4.61376 13.4785C4.50841 13.3732 4.44922 13.2303 4.44922 13.0813C4.44922 12.9323 4.50841 12.7894 4.61376 12.6841C4.71911 12.5787 4.862 12.5195 5.01099 12.5195H11.0091C11.1581 12.5195 11.3009 12.5787 11.4063 12.6841C11.5117 12.7894 11.5708 12.9323 11.5708 13.0813C11.5708 13.2303 11.5117 13.3732 11.4063 13.4785C11.3009 13.5839 11.1581 13.6431 11.0091 13.6431Z" fill="currentColor" />
  </svg>
);

const TAB_ICONS = [TabAllIcon, TabCommonIcon, TabMineIcon, TabSubscribeIcon, TabSystemIcon];

// 导入技能图标（16×16，#666）：还原 figma node 2707:69068（向左上斜出的导入箭头 + 文件夹）。
const ImportIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M14.365 4.88313C13.9832 4.88313 13.7286 5.13768 13.7286 5.51949V12.9649C13.7286 13.4104 13.4104 13.7286 12.965 13.7286H3.03771C2.59225 13.7286 2.27407 13.4104 2.27407 12.9649V3.03768C2.27407 2.59222 2.59225 2.27404 3.03771 2.27404H6.66498C7.0468 2.27404 7.30134 2.01949 7.30134 1.63768C7.30134 1.25586 7.0468 1.00131 6.66498 1.00131H3.03771C1.89225 1.00131 1.00134 1.89222 1.00134 3.03768V12.9649C1.00134 14.1104 1.89225 15.0013 3.03771 15.0013H12.965C14.1104 15.0013 15.0013 14.1104 15.0013 12.9649V5.51949C15.0013 5.20131 14.7468 4.88313 14.365 4.88313Z" fill="#666666" />
    <path d="M5.2637 7.55586C5.00916 7.30131 4.62734 7.30131 4.37279 7.55586C4.11825 7.8104 4.11825 8.19222 4.37279 8.44677L7.55461 11.6286C7.61825 11.6922 7.68188 11.7559 7.74552 11.7559C7.80916 11.8195 7.93643 11.8195 8.00007 11.8195C8.0637 11.8195 8.19098 11.8195 8.25461 11.7559C8.31825 11.6922 8.38188 11.6922 8.44552 11.6286L11.6273 8.44677C11.8819 8.19222 11.8819 7.8104 11.6273 7.55586C11.3728 7.30131 10.991 7.30131 10.7364 7.55586L8.63643 9.65586V8.00131C8.63643 4.81949 11.1819 2.27404 14.3637 2.27404C14.7455 2.27404 15.0001 2.01949 15.0001 1.63768C15.0001 1.25586 14.7455 1.00131 14.3637 1.00131C10.4819 1.00131 7.3637 4.11949 7.3637 8.00131V9.65586L5.2637 7.55586Z" fill="#666666" />
  </svg>
);

// 基于会话创建技能图标（16×16，白色）：还原 figma node 2707:69073（三颗闪光/AI 生成星）。
const CreateIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <g clipPath="url(#rte-skill-create-clip)">
      <path d="M11.6295 1.6535L11.25 0.5L10.8705 1.6535C10.8155 1.82079 10.7219 1.97283 10.5974 2.09736C10.4728 2.2219 10.3208 2.31545 10.1535 2.3705L9 2.75L10.1535 3.1295C10.3208 3.18455 10.4728 3.2781 10.5974 3.40264C10.7219 3.52717 10.8155 3.67921 10.8705 3.8465L11.25 5L11.6295 3.8465C11.6845 3.67921 11.7781 3.52717 11.9026 3.40264C12.0272 3.2781 12.1792 3.18455 12.3465 3.1295L13.5 2.75L12.3465 2.3705C12.1792 2.31545 12.0272 2.2219 11.9026 2.09736C11.7781 1.97283 11.6845 1.82079 11.6295 1.6535ZM5.5 2.5L6.0725 4.24C6.31713 4.98377 6.73301 5.6597 7.28666 6.21334C7.8403 6.76699 8.51623 7.18287 9.26 7.4275L11 8L9.26 8.5725C8.51623 8.81713 7.8403 9.23301 7.28666 9.78666C6.73301 10.3403 6.31713 11.0162 6.0725 11.76L5.5 13.5L4.9275 11.76C4.68287 11.0162 4.26699 10.3403 3.71334 9.78666C3.1597 9.23301 2.48377 8.81713 1.74 8.5725L0 8L1.74 7.4275C2.48377 7.18287 3.1597 6.76699 3.71334 6.21334C4.26699 5.6597 4.68287 4.98377 4.9275 4.24L5.5 2.5ZM13.67 11.278L13.25 10L12.83 11.278C12.7493 11.5235 12.612 11.7466 12.4293 11.9293C12.2466 12.112 12.0235 12.2493 11.778 12.33L10.5 12.75L11.778 13.17C12.0235 13.2507 12.2466 13.388 12.4293 13.5707C12.612 13.7534 12.7493 13.9765 12.83 14.222L13.25 15.5L13.67 14.222C13.7507 13.9765 13.888 13.7534 14.0707 13.5707C14.2534 13.388 14.4765 13.2507 14.722 13.17L16 12.75L14.722 12.33C14.4765 12.2493 14.2534 12.112 14.0707 11.9293C13.888 11.7466 13.7507 11.5235 13.67 11.278Z" fill="white" />
    </g>
    <defs>
      <clipPath id="rte-skill-create-clip">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

// ── 子组件 ─────────────────────────────────────────────────
// 卡片标题行右侧 hover 才显示的三个操作图标（16×16，#666）：分享 / 编辑 / 删除。
// 矢量 1:1 还原 figma（node 2707:68864 分享 / 68866 编辑 / 68869 删除）。
const ShareIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.60187 14.5546C9.23569 14.5546 8.94275 14.4081 8.6498 14.1884C8.35686 13.8955 8.13716 13.5293 8.13716 13.0899V11.6251C6.52597 11.6251 3.88948 12.0646 2.49801 13.9687C2.2783 14.3349 1.76565 14.5546 1.32623 14.6278H1.10653C0.374166 14.4813 -0.0652517 13.8222 0.00799105 13.0899C0.374166 9.94073 1.47272 7.67041 3.37683 6.13247C4.76831 5.03394 6.3795 4.44805 8.13716 4.30157V2.9101C8.13716 2.25098 8.4301 1.66509 9.01599 1.44538C9.52864 1.29892 10.1145 1.37215 10.5539 1.81157L15.241 6.57189C15.6072 6.86484 15.8269 7.30424 15.9001 7.81689C15.9734 8.32955 15.8269 8.8422 15.4607 9.20837C15.3875 9.28161 15.3143 9.42807 15.1678 9.50132L10.6272 14.1152C10.3342 14.4081 9.96804 14.5546 9.60187 14.5546ZM8.43009 10.1604H9.60187V13.0899L14.2157 8.32955L14.289 8.25631C14.3622 8.18308 14.3622 8.0366 14.3622 7.96336C14.3622 7.81689 14.289 7.74366 14.2157 7.67041L14.1425 7.59718L9.52864 2.9101V5.69306L8.50333 5.7663C6.96539 5.7663 5.50066 6.27894 4.25567 7.23101C2.79094 8.40278 1.83889 10.3069 1.54595 12.7969C3.37683 10.5266 6.5992 10.1604 8.43009 10.1604Z" fill="currentColor" />
  </svg>
);

const EditIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.85 7.35C13.4903 7.35 13.2 7.64033 13.2 8V12.3333C13.2 12.8114 12.8114 13.2 12.3333 13.2H3.66667C3.18856 13.2 2.8 12.8114 2.8 12.3333V3.66667C2.8 3.18856 3.18856 2.8 3.66667 2.8H8C8.35967 2.8 8.65 2.50967 8.65 2.15C8.65 1.79033 8.35967 1.5 8 1.5H3.23333C2.27567 1.5 1.5 2.27567 1.5 3.23333V12.7667C1.5 13.7243 2.27567 14.5 3.23333 14.5H12.7667C13.7243 14.5 14.5 13.7243 14.5 12.7667V8C14.5 7.64033 14.2097 7.35 13.85 7.35Z" fill="currentColor" />
    <path d="M5.7483 10.4682C6.00108 10.721 6.41419 10.721 6.66697 10.4682L13.7144 3.42075C13.9672 3.16797 13.9672 2.75486 13.7144 2.50208C13.4616 2.24931 13.0485 2.24931 12.7957 2.50208L5.7483 9.54808C5.49552 9.80086 5.49552 10.2154 5.7483 10.4682Z" fill="currentColor" />
  </svg>
);

const DeleteIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.12488 5.02012H11.8779L11.1331 12.6138C11.1164 12.7828 11.0373 12.9396 10.9114 13.0536C10.7855 13.1676 10.6217 13.2307 10.4518 13.2306H5.55096C5.38127 13.2304 5.21769 13.1672 5.09198 13.0533C4.96627 12.9393 4.88739 12.7827 4.87067 12.6138L4.12488 5.02012ZM3.80233 3.74945C3.66582 3.74934 3.53081 3.77782 3.40598 3.83306C3.28116 3.8883 3.16929 3.96908 3.07757 4.07018C2.98586 4.17128 2.91634 4.29048 2.8735 4.42008C2.83065 4.54969 2.81542 4.68683 2.8288 4.82268L3.60586 12.738C3.65342 13.2208 3.87886 13.6688 4.23837 13.9947C4.59787 14.3206 5.06573 14.5012 5.55096 14.5012H10.4518C10.9371 14.5012 11.4049 14.3206 11.7644 13.9947C12.1239 13.6688 12.3494 13.2208 12.3969 12.738L13.175 4.82268C13.1883 4.68683 13.1731 4.54969 13.1303 4.42008C13.0874 4.29048 13.0179 4.17128 12.9262 4.07018C12.8345 3.96908 12.7226 3.8883 12.5978 3.83306C12.4729 3.77782 12.3379 3.74934 12.2014 3.74945H3.80135H3.80233Z" fill="currentColor" />
    <path d="M1.5 4.28322C1.5 4.11472 1.56694 3.95312 1.68608 3.83397C1.80523 3.71482 1.96683 3.64789 2.13533 3.64789H13.8646C13.948 3.64787 14.0306 3.6643 14.1077 3.69622C14.1848 3.72814 14.2549 3.77494 14.3139 3.83394C14.3729 3.89293 14.4197 3.96298 14.4516 4.04007C14.4836 4.11715 14.5 4.19978 14.5 4.28322C14.5 4.36666 14.4836 4.44929 14.4516 4.52638C14.4197 4.60347 14.3729 4.67351 14.3139 4.73251C14.2549 4.7915 14.1848 4.8383 14.1077 4.87022C14.0306 4.90214 13.948 4.91857 13.8646 4.91855H2.13533C1.96683 4.91855 1.80523 4.85162 1.68608 4.73247C1.56694 4.61332 1.5 4.45172 1.5 4.28322Z" fill="currentColor" />
    <path d="M5.02081 3.11277C5.02081 2.22135 5.74314 1.5 6.63358 1.5H9.3704C10.2618 1.5 10.9832 2.22135 10.9832 3.11277V4.92102H5.02081V3.11277ZM6.63358 2.77067C6.58866 2.77067 6.54417 2.77952 6.50266 2.79671C6.46116 2.8139 6.42345 2.8391 6.39168 2.87087C6.35991 2.90263 6.33471 2.94035 6.31752 2.98185C6.30033 3.02336 6.29148 3.06784 6.29148 3.11277V3.65036H9.7125V3.11277C9.7125 3.06784 9.70366 3.02336 9.68646 2.98185C9.66927 2.94035 9.64407 2.90263 9.61231 2.87087C9.58054 2.8391 9.54282 2.8139 9.50132 2.79671C9.45981 2.77952 9.41533 2.77067 9.3704 2.77067H6.63358Z" fill="currentColor" />
  </svg>
);

const UseIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 8h8M8 4.5 11.5 8 8 11.5" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── 子组件 ─────────────────────────────────────────────────
// 顶部「分享码」紫色标题行 + 灰底输入框（只读分享码 + 右侧复制按钮）。
// 复制按钮两态：默认紫底「复制」，点击复制到剪贴板后变绿底「已复制」（2 秒后复位）。
const ShareCopyIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M8.87301 3.52364C9.08579 3.52364 9.28984 3.60817 9.4403 3.75862C9.59075 3.90907 9.67527 4.11313 9.67527 4.3259V11.8137C9.67527 12.0265 9.59075 12.2305 9.4403 12.381C9.28984 12.5314 9.08579 12.6159 8.87301 12.6159H2.98976C2.88441 12.6159 2.78008 12.5952 2.68275 12.5549C2.58541 12.5146 2.49697 12.4555 2.42248 12.381C2.34798 12.3065 2.28889 12.218 2.24857 12.1207C2.20825 12.0234 2.1875 11.919 2.1875 11.8137V4.3259C2.1875 4.11313 2.27202 3.90907 2.42248 3.75862C2.57293 3.60817 2.77699 3.52364 2.98976 3.52364H8.87301ZM8.60559 4.59332H3.25718V11.5463H8.60559V4.59332ZM11.0102 1.38428C11.2098 1.38415 11.4022 1.45838 11.5499 1.59248C11.6977 1.72658 11.7901 1.91092 11.8093 2.10952L11.8125 2.18654V9.40315C11.8123 9.53947 11.7601 9.67059 11.6666 9.76971C11.573 9.86884 11.4451 9.92849 11.309 9.93648C11.1729 9.94447 11.0389 9.90019 10.9344 9.8127C10.8298 9.7252 10.7627 9.60109 10.7466 9.46573L10.7428 9.40315V2.45396H5.39655C5.26555 2.45394 5.13911 2.40585 5.04121 2.3188C4.94332 2.23175 4.88078 2.1118 4.86545 1.9817L4.86171 1.91912C4.86172 1.78812 4.90982 1.66168 4.99687 1.56379C5.08392 1.46589 5.20387 1.40335 5.33397 1.38802L5.39655 1.38428H11.0102Z" fill="currentColor" />
  </svg>
);
const ShareCheckIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M1.5548 5.88011C1.4304 5.76419 1.26586 5.70108 1.09584 5.70408C0.925831 5.70708 0.76362 5.77595 0.643383 5.89619C0.523147 6.01643 0.454274 6.17864 0.451274 6.34865C0.448274 6.51867 0.511382 6.68321 0.627302 6.80761L5.0023 11.1826C5.12535 11.3055 5.29215 11.3745 5.46605 11.3745C5.63996 11.3745 5.80676 11.3055 5.9298 11.1826L13.3673 3.74511C13.4832 3.62071 13.5463 3.45617 13.5433 3.28615C13.5403 3.11614 13.4715 2.95393 13.3512 2.83369C13.231 2.71345 13.0688 2.64458 12.8988 2.64158C12.7287 2.63858 12.5642 2.70169 12.4398 2.81761L5.46605 9.79049L1.5548 5.87923V5.88011Z" fill="currentColor" />
  </svg>
);
const ShareTitleIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M9.60188 14.5546C9.23571 14.5546 8.94276 14.4081 8.64982 14.1884C8.35687 13.8955 8.13717 13.5293 8.13717 13.0899V11.6251C6.52599 11.6251 3.88949 12.0646 2.49803 13.9687C2.27831 14.3349 1.76566 14.5546 1.32625 14.6278H1.10654C0.374181 14.4813 -0.0652364 13.8222 0.00800631 13.0899C0.374181 9.94073 1.47273 7.67041 3.37685 6.13247C4.76833 5.03394 6.37951 4.44805 8.13717 4.30157V2.9101C8.13717 2.25098 8.43012 1.66509 9.01601 1.44538C9.52865 1.29892 10.1145 1.37215 10.5539 1.81157L15.241 6.57189C15.6072 6.86484 15.8269 7.30424 15.9002 7.81689C15.9734 8.32955 15.8269 8.8422 15.4607 9.20837C15.3875 9.28161 15.3143 9.42807 15.1678 9.50132L10.6272 14.1152C10.3342 14.4081 9.96806 14.5546 9.60188 14.5546ZM8.4301 10.1604H9.60188V13.0899L14.2157 8.32955L14.289 8.25631C14.3622 8.18308 14.3622 8.0366 14.3622 7.96336C14.3622 7.81689 14.289 7.74366 14.2157 7.67041L14.1425 7.59718L9.52865 2.9101V5.69306L8.50335 5.7663C6.9654 5.7663 5.50068 6.27894 4.25568 7.23101C2.79096 8.40278 1.83891 10.3069 1.54596 12.7969C3.37685 10.5266 6.59922 10.1604 8.4301 10.1604Z" fill="#7C3AED" />
  </svg>
);

const ShareContent: React.FC<{ code: string }> = ({ code }) => {
  const { message } = App.useApp();
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      message.error('复制失败');
    }
  };
  return (
    <div className="rte-skill-share" onClick={(e) => e.stopPropagation()}>
      {/* 标题行：左「分享码」紫标题 + 右灰色提示，space-between 同行（对齐 figma 2707:68609） */}
      <div className="rte-skill-share-head">
        <div className="rte-skill-share-title">
          <ShareTitleIcon />
          <span>分享码</span>
        </div>
        <span className="rte-skill-share-hint">复制后粘贴在「导入技能」即可使用</span>
      </div>
      <div className="rte-skill-share-box">
        <span className="rte-skill-share-code">{code}</span>
        <button
          type="button"
          className={`rte-skill-share-copy${copied ? ' rte-skill-share-copy-done' : ''}`}
          onClick={handleCopy}
        >
          {copied ? <ShareCheckIcon /> : <ShareCopyIcon />}
          <span>{copied ? '已复制' : '复制'}</span>
        </button>
      </div>
    </div>
  );
};

const SkillCardItem: React.FC<{
  card: SkillCard;
  selected: boolean;
  onClick: () => void;
  onShare: () => void | string | Promise<void | string>;
  onEdit: () => void;
  onDelete: () => void;
  onUse: () => void;
  getPopupContainer?: () => HTMLElement;
}> = ({ card, selected, onClick, onShare, onEdit, onDelete, onUse, getPopupContainer }) => {
  // 图标在卡片 onClick 内，点图标须 stopPropagation 阻止冒泡触发卡片选中
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  // 溢出检测（标题 + 描述）
  const [titleRef, titleOverflow, checkTitleOverflow] = useTextOverflow();
  const [descRef, descOverflow, checkDescOverflow] = useTextOverflow('height');
  // 分享码：点分享图标打开弹层时向外取（onShare 可返回 code 字符串/Promise）；
  // 未返回则回退用技能 id 占位（mock 演示）。弹层关闭后清空，下次重新取。
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCode, setShareCode] = useState('');
  // 删除确认弹层开关（受控）：与 shareOpen 一起决定「操作图标是否常驻显示」——
  // 弹层打开后鼠标移到弹层上，卡片失去 :hover，图标本会消失；用这两个状态把图标钉住。
  const [deleteOpen, setDeleteOpen] = useState(false);
  const pinned = shareOpen || deleteOpen;
  const handleShareOpenChange = async (open: boolean) => {
    setShareOpen(open);
    if (!open) {
      setShareCode('');
      return;
    }
    try {
      const code = await onShare();
      setShareCode(typeof code === 'string' && code ? code : `SK-${card.id}`);
    } catch {
      setShareCode(`SK-${card.id}`);
    }
  };
  return (
    <div
      className={`rte-skill-card${selected ? ' rte-skill-card-selected' : ''}${pinned ? ' rte-skill-card-pinned' : ''}`}
      onClick={onClick}
      data-rte-skill-card="true"
      aria-pressed={selected}
    >
      {/* 标题行：标题左 + badge/icon 右（hover 卡片时 badge 隐藏、icon 显示） */}
      <div className="rte-skill-card-header">
        <SeTooltip
          title={titleOverflow ? card.title : ''}
          placement="top"
          mouseEnterDelay={0.3}
          getPopupContainer={getPopupContainer}
          zIndex={10000}
        >
          <div className="rte-skill-card-title" ref={titleRef} onMouseEnter={checkTitleOverflow}>{card.title}</div>
        </SeTooltip>
        <div className={`rte-skill-card-aside${pinned ? ' rte-skill-card-aside-pinned' : ''}`}>
          <span className={`rte-skill-card-badge rte-skill-card-badge-${card.badge}`}>
            {BADGE_LABEL[card.badge]}
          </span>
          {/* {card.badge !== 'system' && ( */}
          <div className={`rte-skill-card-actions${pinned ? ' rte-skill-card-actions-pinned' : ''}`}>
            {card.badge === 'mine' && (
              <Popover
                open={shareOpen}
                onOpenChange={handleShareOpenChange}
                trigger="click"
                placement="bottomRight"
                arrow={false}
                overlayClassName="rte-skill-share-popover"
                zIndex={10000}
                getPopupContainer={getPopupContainer}
                content={<ShareContent code={shareCode} />}
              >
                <button
                  type="button"
                  className={`rte-skill-card-action${shareOpen ? ' rte-skill-card-action-active' : ''}`}
                  title="分享"
                  aria-label="分享"
                  onClick={stop}
                >
                  <ShareIcon />
                </button>
              </Popover>
            )}
            {card.badge === 'mine' && (
              <button
                type="button"
                className="rte-skill-card-action"
                title="编辑"
                aria-label="编辑"
                onClick={(e) => { stop(e); onEdit(); }}
              >
                <EditIcon />
              </button>
            )}
            {card.badge !== 'mine' && (
              <button
                type="button"
                className="rte-skill-card-action"
                title="查看详情"
                aria-label="查看详情"
                onClick={(e) => { stop(e); onEdit(); }}
              >
                <i className="iconfont icon-chakanjinengxiangqing" />
              </button>
            )}
            {card.badge !== 'system' && (
              <Popconfirm
                title="删除技能后不可恢复，确认删除吗？"
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                okText="确认"
                cancelText="取消"
                placement="bottomRight"
                arrow={false}
                overlayClassName="rte-skill-delete-confirm"
                zIndex={10000}
                icon={
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path fillRule="evenodd" clipRule="evenodd" d="M1.5 9C1.5 4.85786 4.85786 1.5 9 1.5C13.1421 1.5 16.5 4.85786 16.5 9C16.5 13.1421 13.1421 16.5 9 16.5C4.85786 16.5 1.5 13.1421 1.5 9ZM8.25 11.25V12.75H9.75V11.25H8.25ZM9.75 10.5L9.75 5.25H8.25L8.25 10.5H9.75Z" fill="#F53F3F" />
                  </svg>
                }
                okButtonProps={{ danger: true }}
                getPopupContainer={getPopupContainer}
                onConfirm={(e) => { e?.stopPropagation(); setDeleteOpen(false); onDelete(); }}
                onCancel={(e) => { e?.stopPropagation(); setDeleteOpen(false); }}
              >
                <button
                  type="button"
                  className={`rte-skill-card-action rte-skill-card-action-delete${deleteOpen ? ' rte-skill-card-action-active' : ''}`}
                  title="删除"
                  aria-label="删除"
                  onClick={stop}
                >
                  <DeleteIcon />
                </button>
              </Popconfirm>
            )}
          </div>
          {/* )} */}
        </div>
      </div>
      {/* 描述限 1 行（CSS line-clamp），完整内容 hover 气泡显示。
          用 SeTooltip（与智能体节点统一的白底圆角无箭头样式）；
          气泡挂浮层宿主避免 Qiankun removeChild 崩溃；mouseEnterDelay 防误触；
          zIndex 10000 盖过技能面板（9999），否则气泡被面板遮住看不见。 */}
      <SeTooltip
        title={descOverflow ? card.desc : ''}
        placement="top"
        mouseEnterDelay={0.3}
        getPopupContainer={getPopupContainer}
        zIndex={10000}
      >
        <div className="rte-skill-card-desc" ref={descRef} onMouseEnter={checkDescOverflow}>{card.desc}</div>
      </SeTooltip>
      {/* <div className="rte-skill-card-footer">
        <span className={`rte-skill-card-badge rte-skill-card-badge-${card.badge}`}>
          {BADGE_LABEL[card.badge]}
        </span>
        热度（非 hover 态显示，mine 类无热度则不显示）
        {card.heat && (
          <span className="rte-skill-card-heat">
            <span className="rte-skill-card-heat-num">{card.heat}</span>
            <span className="rte-skill-card-heat-text">万人正在使用</span>
          </span>
        )}
        「去使用」按钮：hover 才显示，盖住热度位（CSS 控制显隐）
        <button
          type="button"
          className="rte-skill-card-use"
          onClick={(e) => { stop(e); onUse(); }}
        >
          <UseIcon />
          <span>去使用</span>
        </button>
      </div> */}
    </div>
  );
};

// ── 主组件 ─────────────────────────────────────────────────
export const SkillPanel: React.FC<Props> = React.memo(({ selectedId, onSelect, onCardClick, onAction, skillListFetch, onHeaderAction, showCreateSkill = true, createDisabled, panelMaxHeight }) => {
  const { message } = App.useApp();
  const [activeTab, setActiveTab] = useState(0);
  const isCommonTab = TAB_SKILL_TYPE[activeTab] === 'common';
  const [pageIndex, setPageIndex] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // 列表数据：有 skillListFetch 则用接口数据，否则用内置 mock（演示）
  const [cards, setCards] = useState<SkillCard[]>(skillListFetch ? [] : MOCK_CARDS);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // EMPTY_GRACE_MS 内接口返回则不展示 loading 和 empty，避免编辑/刷新串行接口耗时较长时
  // 出现「空态闪一下 → 又被新数据替换」的抖动。值要大于消费端典型 fetch 总耗时（接近 1s）。
  // pending 初值依据「有外部接口」——首次挂载就会立刻 fetch，先把空态藏住等超时或请求回来。
  const [pending, setPending] = useState(!!skillListFetch);
  const loadingTimerRef = useRef(0);
  const showLoading = useCallback(() => {
    setPending(true);
    loadingTimerRef.current = window.setTimeout(() => {
      setPending(false);
      setLoading(true);
    }, 300);
  }, []);
  const hideLoading = useCallback(() => {
    window.clearTimeout(loadingTimerRef.current);
    setPending(false);
    setLoading(false);
  }, []);
  // 搜索词：受控输入框（去掉旧 readOnly），输入后防抖触发重新拉取
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const host = usePopupHost();
  // Popconfirm 挂到浮层宿主，避免 Qiankun 沙箱 removeChild 崩溃（与面板其他浮层一致）
  const getPopupContainer = host ? () => host : undefined;
  // 并发竞态防护：仅采纳最新一次请求的结果（切 tab/连续输入时旧请求可能后到）
  const fetchTokenRef = useRef(0);
  const loadingPageRef = useRef(false);
  const hasMoreRef = useRef(true);
  // 滚动时显示滚动条，停止 800ms 后隐藏
  const cardsRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef(0);
  // 输入防抖 300ms：keyword → debouncedKeyword（真正触发请求的值）
  useEffect(() => {
    const t = window.setTimeout(() => {
      setPageIndex(1);
      setDebouncedKeyword(keyword.trim());
    }, 300);
    return () => window.clearTimeout(t);
  }, [keyword]);

  const handleTabChange = useCallback((nextTab: number) => {
    setPageIndex(1);
    setActiveTab(nextTab);
  }, []);

  // 拉取技能列表：按当前 tab、搜索词和页码请求；后续页追加到已有卡片末尾。
  // mount/切 tab/搜索词变化时从第 1 页开始，滚动接近底部时加载下一页。
  // 常用技能由后端一次性返回，第 1 页之后不再发起分页请求。
  const fetchList = useCallback(async (requestedPage: number, append: boolean) => {
    if (!skillListFetch) return;
    if (append && isCommonTab) return;
    if (append && (loadingPageRef.current || !hasMoreRef.current)) return;
    loadingPageRef.current = true;
    const token = ++fetchTokenRef.current;
    if (append) {
      setLoadingMore(true);
    } else {
      hasMoreRef.current = true;
      setHasMore(true);
      showLoading();
    }
    try {
      const result = await skillListFetch({
        skillType: TAB_SKILL_TYPE[activeTab],
        pageIndex: requestedPage,
        pageSize: PAGE_SIZE,
        keyword: debouncedKeyword || undefined,
      });
      if (token !== fetchTokenRef.current) return; // 已有更新的请求，丢弃本次
      const list = Array.isArray(result) ? result : result.list;
      const nextCards = list.map(toCard);
      const nextTotal = Array.isArray(result) ? undefined : result.total;
      setCards((currentCards) => append ? [...currentCards, ...nextCards] : nextCards);
      setPageIndex(requestedPage);
      const nextHasMore = !isCommonTab && (nextTotal !== undefined ? requestedPage * PAGE_SIZE < nextTotal : nextCards.length >= PAGE_SIZE);
      hasMoreRef.current = nextHasMore;
      setHasMore(nextHasMore);
    } catch {
      if (token === fetchTokenRef.current) message.error('技能列表加载失败');
    } finally {
      if (token === fetchTokenRef.current) {
        loadingPageRef.current = false;
        hideLoading();
        setLoadingMore(false);
      }
    }
  }, [skillListFetch, activeTab, debouncedKeyword, isCommonTab, showLoading, hideLoading]);

  // 面板打开即拉取，并在 tab / 搜索词变化时重新拉取（SkillButton 仅 open 时挂载本组件）
  useEffect(() => {
    void fetchList(1, false);
  }, [fetchList]);

  // 切 tab / 搜索词变化时把滚动条归位——cards 容器 DOM 在 tab 间复用，
  // 上一个 tab 滚到底后切过去会停在原 scrollTop（看起来像「空白」或直接到底）。
  useEffect(() => {
    cardsRef.current?.scrollTo({ top: 0 });
  }, [activeTab, debouncedKeyword]);

  const handleCardsScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    element.classList.add('is-scrolling');
    window.clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = window.setTimeout(() => {
      element.classList.remove('is-scrolling');
    }, 300);

    const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (!isCommonTab && distanceToBottom <= 80 && hasMore && !loadingPageRef.current) {
      void fetchList(pageIndex + 1, true);
    }
  }, [fetchList, hasMore, isCommonTab, pageIndex]);

  // 当前页内容不足以形成滚动区域时，自动继续预加载，避免用户无法通过滚动触发下一页。
  useEffect(() => {
    if (isCommonTab || !skillListFetch || loading || loadingMore || !hasMore) return;
    const element = cardsRef.current;
    if (!element || element.scrollHeight > element.clientHeight) return;
    void fetchList(pageIndex + 1, true);
  }, [cards.length, fetchList, hasMore, isCommonTab, loading, loadingMore, pageIndex, skillListFetch]);

  const panelStyle: React.CSSProperties | undefined = panelMaxHeight === undefined
    ? undefined
    : { maxHeight: panelMaxHeight };

  const handleCardClick = (card: SkillCard) => {
    // 点击卡片即选中（等同于原「去使用」），同时通知外部
    onCardClick?.({ id: card.id, title: card.title });
    onSelect?.({ id: card.id, title: card.title, desc: card.desc || undefined, usageInstruction: card.usageInstruction });
  };

  const handleDelete = async (card: SkillCard) => {
    // 把删除事件抛给消费端（业务侧调真实删除接口）。
    // 有 skillListFetch（接入真实数据）时：await 删除完成 → 重新拉列表刷新，不做前端本地移除。
    // 无 skillListFetch（mock 演示）时：本地 filter 移除即可。
    try {
      await onAction?.('delete', { id: card.id, title: card.title });
    } catch {
      message.error('删除失败');
      return;
    }
    // 删的若是当前选中技能，同步取消选中
    if (selectedId === card.id) onSelect?.(null);
    if (skillListFetch) {
      setPageIndex(1);
      await fetchList(1, false);
    } else {
      setCards((prev) => prev.filter((c) => c.id !== card.id));
    }
    message.success('已删除技能');
  };

  return (
    <div className="rte-skill-panel" style={panelStyle}>
      {/* 顶部：搜索 + 导入 + 创建按钮 */}
      <div className="rte-skill-panel-header">
        <div className="rte-skill-search">
          <input
            className="rte-skill-search-input"
            placeholder="请输入想搜索的技能名称或描述"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <span className="rte-skill-search-icon">
            <SearchIcon />
          </span>
        </div>
        <div className="rte-skill-actions">
          <button type="button" className="rte-skill-import-btn" onClick={() => onHeaderAction?.('import')}>
            <ImportIcon />
            <span>导入技能</span>
          </button>
          {showCreateSkill && (
            <button type="button" className={`rte-skill-create-btn${createDisabled ? ' rte-skill-create-btn-disabled' : ''}`} disabled={createDisabled} onClick={() => !createDisabled && onHeaderAction?.('create')}>
              {/* <CreateIcon /> */}
              <i className='iconfont icon-jiyudangqianduihuachuangjianjineng'></i>
              <span>基于当前会话创建技能</span>
            </button>
          )}
        </div>
      </div>
      {/* 下半部分：左侧 tab + 右侧卡片 */}
      <div className="rte-skill-panel-body">
        <div className="rte-skill-sidebar">
          {TABS.map((tab, i) => {
            const Icon = TAB_ICONS[i];
            return (
              <div
                key={tab}
                className={`rte-skill-sidebar-item${i === activeTab ? ' rte-skill-sidebar-item-active' : ''}`}
                onClick={() => handleTabChange(i)}
              >
                <Icon />
                <span>{tab}</span>
              </div>
            );
          })}
        </div>
        {/* 卡片网格：loading 时显示居中 Spin，空列表显示空态 */}
        <div className="rte-skill-results">
          {loading ? (
            <div className="rte-skill-cards-status">
              <Spin />
            </div>
          ) : cards.length === 0 ? (
            // pending 期间（首次挂载等 fetch / 200ms 内）藏住空态内容，避免闪烁
            <div className="rte-skill-cards-status">
              {!pending && (
                <>
                  <img
                    className="rte-skill-empty-img"
                    src="https://sealseek-public-read.oss-cn-hangzhou.aliyuncs.com/file/website/kongzhuangtai.svg"
                    alt="暂无数据"
                  />
                  <span className="rte-skill-empty-text">暂无技能</span>
                </>
              )}
            </div>
          ) : (
            <div className="rte-skill-cards" ref={cardsRef} onScroll={handleCardsScroll}>
              {cards.map((c) => (
                <SkillCardItem
                  key={c.id}
                  card={c}
                  selected={selectedId === c.id}
                  onClick={() => handleCardClick(c)}
                  onShare={() => onAction?.('share', { id: c.id, title: c.title })}
                  onEdit={() => onAction?.('edit', { id: c.id, title: c.title })}
                  onDelete={() => handleDelete(c)}
                  onUse={() => onSelect?.({ id: c.id, title: c.title, desc: c.desc || undefined, usageInstruction: c.usageInstruction })}
                  getPopupContainer={getPopupContainer}
                />
              ))}
              {!isCommonTab && loadingMore && (
                <div className="rte-skill-cards-loading-more">
                  <Spin size="small" />
                </div>
              )}
              {!isCommonTab && !loadingMore && !hasMore && <div className="rte-skill-cards-end">暂无更多技能～</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
SkillPanel.displayName = 'SkillPanel';
